'use client';

import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { LocaleCode, NARRATION_AUDIO } from '@/translations/config';

interface AudioContextType {
  isPlaying: boolean;
  duration: number;
  currentTime: number;
  hasPlayedBefore: boolean;
  isLoading: boolean;
  isVisible: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  togglePlayPause: () => void;
  showAudioPlayer: () => void;
  setCurrentTime: (time: number) => void;
  formatTime: (time: number) => string;
  setIsVisible: (value: boolean) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

// Each language has its own recording, so each needs its own saved position:
// resuming the Spanish narration at the English offset would land mid-sentence.
const localStorageKey = (locale: '' | LocaleCode) =>
  locale ? `audioPlayerLastPosition-new-${locale}` : 'audioPlayerLastPosition-new';

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasPlayedBefore, setHasPlayedBefore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [hasPlayedBelow1000px, setHasPlayedBelow1000px] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Treat translated versions (/es, /es/summary, ...) like their English counterparts
  const pathname = usePathname();
  const locale = ((pathname ?? '/').match(/^\/(es|fr|it)(?=\/|$)/)?.[1] ?? '') as '' | LocaleCode;
  const LOCAL_STORAGE_KEY = localStorageKey(locale);

  // Handle client-side only rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle scroll events
  useEffect(() => {
    if (!isClient) return;

    const handleScroll = () => {
      if (isPlaying && window.scrollY > 1000) {
        setHasPlayedBelow1000px(true);
      }
      if (!isPlaying && window.scrollY > 1000 && !hasPlayedBelow1000px) {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isClient, isPlaying, hasPlayedBelow1000px]);

  // Create and set up audio element
  useEffect(() => {
    if (!isClient) return;

    // Create audio element
    const audio = new Audio(NARRATION_AUDIO[locale]);
    audio.preload = 'auto';
    audioRef.current = audio;
    // A language switch swaps in a different recording, so the old file's
    // duration and position no longer describe what the player is holding.
    setIsLoading(true);
    setDuration(0);
    setCurrentTime(0);

    // Set up event listeners
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      // --- Retrieve saved position ---
      if (isClient) {
        const savedTime = localStorage.getItem(LOCAL_STORAGE_KEY); // Use constant
        if (savedTime) {
          const numericTime = parseFloat(savedTime);
          // Check if the saved time is valid and within the duration
          if (!isNaN(numericTime) && numericTime > 0 && numericTime < audio.duration) {
            audio.currentTime = numericTime;
            // Update state immediately after setting audio time
            setCurrentTime(numericTime);
          } else {
            // Clear invalid stored time
            localStorage.removeItem(LOCAL_STORAGE_KEY); // Use constant
          }
        }
      }
    };
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Reset saved position when audio finishes
      if (isClient) {
        localStorage.removeItem(LOCAL_STORAGE_KEY); // Use constant
      }
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    // Force load the audio
    audio.load();

    return () => {
      // Clean up event listeners
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause(); // Ensure audio stops on unmount
    };
    // Re-created when the reader switches language, so the player follows the page
  }, [isClient, locale, LOCAL_STORAGE_KEY]);

  // Control audio playback when isPlaying changes
  useEffect(() => {
    if (!audioRef.current || !isClient) return;

    if (isPlaying) {
      const playPromise = audioRef.current.play();
      
      // Handle the play promise to avoid "play() failed because the user didn't interact with the document first" errors
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            if (!hasPlayedBefore) {
              setHasPlayedBefore(true);
            }
          })
          .catch(error => {
            // Auto-play was prevented
            setIsPlaying(false);
            console.error("Playback was prevented:", error);
          });
      }
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, hasPlayedBefore, isClient]); // Added isClient dependency

  // Update playback rate when it changes, and re-apply it to the fresh audio
  // element created when the reader switches language
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, locale]);

  // *** Add: Periodically save position while playing ***
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isPlaying && isClient && audioRef.current) {
      intervalId = setInterval(() => {
        if (audioRef.current) {
          const current = audioRef.current.currentTime;
          const durationVal = audioRef.current.duration;
          // Save only if playback has started and not at the very beginning or end
          if (current > 0 && current < durationVal && !isNaN(durationVal) && durationVal > 0) {
            localStorage.setItem(LOCAL_STORAGE_KEY, String(current)); // Use constant
          }
        }
      }, 2000); // Save every 2 seconds
    }

    // Cleanup function to clear the interval
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, isClient, LOCAL_STORAGE_KEY]); // Re-run this effect when isPlaying, isClient, or the language's storage key changes
  // *** End Add ***

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
    if (!isVisible) {
      setIsVisible(true);
    }
  };

  const showAudioPlayer = () => {
    setIsVisible(true);
  };

  // Format time in mm:ss
  const formatTime = (time: number) => {
    if (isNaN(time) || time === 0) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  // Set current time directly (for seeking)
  const handleSetCurrentTime = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  return (
    <AudioContext.Provider value={{ 
      isPlaying, 
      duration, 
      currentTime,
      hasPlayedBefore,
      isLoading,
      isVisible,
      audioRef,
      togglePlayPause, 
      showAudioPlayer,
      setCurrentTime: handleSetCurrentTime,
      formatTime,
      setIsVisible,
      playbackRate,
      setPlaybackRate
    }}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
} 